<template>
  <div class="myTimeTrackingTable">
    <DataTable
      class="p-datatable-sm"
      :rowClass="rowClass"
      :value="timeTrackings"
      :rowHover="true"
      :loading="isLoading"
      editMode="cell"
      dataKey="number"
      :lazy="true"
      :totalRecords="getTimeTrackingCount"
      stateStorage="local"
      responsiveLayout="scroll"
      :sortField="tableState.sortField"
      :sortOrder="tableState.sortOrder"
      :rows="tableState.pagination.rowsPerPage"
      :first="tableState.pagination.page * tableState.pagination.rowsPerPage"
      @page="onPage"
      @sort="onSort"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :rowsPerPageOptions="[25]"
    >
      <Column
        field="startTimeDay"
        :header="$t('day')"
        :headerStyle="{ width: '100px' }"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data.createdAt">
            {{ formatDateWithDots(slotProps.data.startTime) }}
          </span>
        </template>
      </Column>

      <Column field="startTime" :header="$t('start')">
        <template #body="slotProps">
          <strong> {{ $dayjs(slotProps.data[slotProps.column.field]).format('HH:mm') }}</strong>
        </template>

        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.value"
            @input="filterCallback()"
            selectionMode="single"
            dateFormat="dd.mm.yy"
            placeholder=">="
          >
          </Calendar>
        </template>
      </Column>

      <Column field="stopTime" :header="$t('end')">
        <template #body="slotProps">
          <strong v-if="slotProps.data[slotProps.column.field]">
            {{ $dayjs(slotProps.data[slotProps.column.field]).format('HH:mm') }}
          </strong>
          <strong v-else>
            <LoopingRhombusesSpinner
              :animation-duration="12000"
              :rhombus-size="9"
              color="#495057"
            />
          </strong>
        </template>

        <template #filter="{filterModel,filterCallback}">
          <Calendar
            v-model="filterModel.value"
            @input="filterCallback()"
            selectionMode="single"
            dateFormat="dd.mm.yy"
            placeholder=">="
          >
          </Calendar>
        </template>
      </Column>

      <Column field="duration" :header="$t('duration')">
        <template #body="slotProps">
          <span>
            {{ formatDuration(slotProps.data.duration || 0) }}
          </span>
        </template>
      </Column>

      <Column field="timeTrackingState" :header="$t('state')" :styles="{ width: '7%' }">
        <template #body="slotProps">
          <b-badge :variant="getTimeTrackingStateColor(slotProps.data.timeTrackingState)">
            {{ $t(slotProps.data.timeTrackingState) }}
          </b-badge>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Calendar from 'primevue/calendar';
import { FilterMatchMode } from 'primevue/api';
import { getTimeTrackingStateColor } from '@/helpers/colors';
import { LoopingRhombusesSpinner } from 'epic-spinners';

export default {
  name: 'MyTimeTrackingTable',
  components: {
    DataTable,
    Column,
    Calendar,
    LoopingRhombusesSpinner,
  },

  data() {
    return {
      filteredUsers: [],
      isEditingAllowed: true,
      filteredProjects: [],
      projectsFilter: {
        pagination: {
          page: 0,
          rowsPerPage: 25,
        },
        sortField: 'createdAt',
        sortOrder: -1,
        filterName: 'my-time-tracking-table-filters',
      },

      defaultFilters: {
        number: { value: null, matchMode: FilterMatchMode.EQUALS },
      },
      defaultCustomFilters: {
        taskStates: [],
      },

      tableState: {
        pagination: {
          page: 0,
          rowsPerPage: 25,
        },
        filters: {
          createdAt: { value: null, matchMode: FilterMatchMode.DATE },
        },
        sortField: 'createdAt',
        sortOrder: -1,
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['isAdmin', 'getCurrentUser', 'isClient']),
    ...mapGetters([
      'getTimeTrackings',
      'getEnumValues',
      'isLoading',
      'getUser',
      'getTimeTrackingCount',
    ]),

    user() {
      return this.getCurrentUser ? this.getUser(this.getCurrentUser.id) : null;
    },

    timeTrackings() {
      const sortedTrackings = [...this.getTimeTrackings];

      sortedTrackings.sort((a, b) => {
        const dateA = new Date(a.startTime || a.createdAt);
        const dateB = new Date(b.startTime || b.createdAt);
        return dateB - dateA;
      });

      return sortedTrackings.sort((a, b) => {
        if (
          (a.timeTrackingState === 'STARTED' || a.timeTrackingState === 'gestartet') &&
          b.timeTrackingState !== 'STARTED' &&
          b.timeTrackingState !== 'gestartet'
        ) {
          return -1;
        }
        if (
          (b.timeTrackingState === 'STARTED' || b.timeTrackingState === 'gestartet') &&
          a.timeTrackingState !== 'STARTED' &&
          a.timeTrackingState !== 'gestartet'
        ) {
          return 1;
        }
        return 0;
      });
    },

    timeTrackingStates() {
      const timeTrackingStates = this.getEnumValues('TimeTrackingStates').map(
        (timeTrackingState) => {
          return { value: timeTrackingState, label: this.$t(timeTrackingStates) };
        }
      );
      return timeTrackingStates;
    },
  },
  methods: {
    ...mapActions(['fetchEnumValues', 'fetchTimeTrackingsByUser']),
    formatDateWithDots(datetime) {
      const dateObj = new Date(datetime);
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const year = dateObj.getFullYear();
      return `${day}.${month}.${year}`;
    },

    async loadTimeTrackingData() {
      try {
        if (!this.getCurrentUser || !this.getCurrentUser.id) {
          console.warn('Benutzer ist nicht verfügbar. Zeiterfassungsdaten werden nicht geladen.');
          return;
        }

        await this.fetchTimeTrackingsByUser({
          userId: this.getCurrentUser.id,
          page: 0,
          pageSize: 25,
          sortField: 'createdAt',
          sortOrder: -1,
        });
      } catch (error) {
        console.error('Fehler beim Laden der Zeitverfolgungsdaten:', error);
      }
    },

    rowClass(data) {
      return data.number === '_new' ? 'new' : null;
    },

    formatDuration(minutes) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`;
    },

    async onPage(event) {
      this.tableState.pagination.page = event.page;
      this.tableState.pagination.rowsPerPage = event.rows;
      this.loadTimeTrackingData();
    },

    async onSort(event) {
      this.tableState.sortField = event.sortField;
      this.tableState.sortOrder = event.sortOrder;
      this.loadTimeTrackingData();
    },
    getTimeTrackingStateColor,
  },
  async created() {
    try {
      await this.fetchEnumValues('TimeTrackingState');

      if (this.getCurrentUser && this.getCurrentUser.id) {
        await this.loadTimeTrackingData();
      } else {
        console.warn('Benutzer ist noch nicht authentifiziert.');
      }
    } catch (error) {
      console.error('Fehler beim Abrufen der Zeitverfolgungsdaten:', error);
    }
  },
  async mounted() {
    await this.fetchEnumValues('TimeTrackingState');
  },
};
</script>

<style scoped>
.p-datatable-tbody > tr > td {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.p-datatable-tbody > tr > td .p-column-title {
  display: none;
}
</style>
